import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Shield, Check, Sparkles, Lock, Unlock, Package, Camera } from 'lucide-react';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';

/**
 * PremiumPage — Ad-Free Premium Tier
 *
 * Premium is intentionally lean: ad-free experience + a custom profile photo
 * (auto safety-checked). No analytics, AI suggestions, or simulation tools are
 * gated. Future Special Category Packs (Scientists, Painters, etc.) will appear
 * under "Future Packages".
 */
export default function PremiumPage({ onBack }) {
  const { user, refreshUser } = useAuth();
  const [upgrading, setUpgrading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const isPremium = user?.premium;
  const ownedPackages = user?.premium_packages || []; // placeholder for future

  // task 0 — display the live price from the backend catalogue (the same
  // source of truth that builds the Stripe charge), so the shown price can
  // never drift from what's actually charged. Falls back to 4.99 on error.
  const [adFreePrice, setAdFreePrice] = useState('4.99');
  useEffect(() => {
    let mounted = true;
    api.get('/billing/catalog')
      .then((r) => {
        const item = (r.data?.items || []).find((i) => i.code === 'remove_ads_forever');
        if (mounted && item?.amount_cents != null) {
          setAdFreePrice((item.amount_cents / 100).toFixed(2));
        }
      })
      .catch(() => { /* keep fallback price */ });
    return () => { mounted = false; };
  }, []);

  const handleRestore = async () => {
    setRestoring(true);
    try {
      // Cross-platform Restore Purchases — re-derives entitlements from the
      // durable purchase ledger (Stripe + Apple/Google), so a purchase made
      // on iOS/Android is restored here on the web too.
      const r = await api.post('/billing/restore');
      const restored = r.data?.restored || [];
      if (r.data?.entitlements?.premium || restored.length) {
        await refreshUser?.();
        toast.success(
          restored.length
            ? 'Purchases restored — you\'re ad-free!'
            : 'Your purchases are already active.'
        );
      } else {
        toast.info('No previous purchases found for this account.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed — please try again.');
    } finally { setRestoring(false); }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      // v5.7.61 — Real Stripe Checkout (test mode). Hits the new
      // /billing/checkout endpoint which creates a hosted session for
      // the `remove_ads_forever` SKU and returns its URL; we redirect
      // the browser there to complete payment. The webhook flips
      // user.premium = true on success, and the FE success page
      // polls /billing/session-status to reflect the change.
      const r = await api.post('/billing/checkout', { code: 'remove_ads_forever' });
      if (r.data?.already_owned) {
        toast.info(r.data.message || 'You already have Premium.');
        window.location.reload();
        return;
      }
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      toast.error('Unexpected checkout response — try again later.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed — please try again.');
    } finally { setUpgrading(false); }
  };

  return (
    <div className="p-4" data-testid="premium-page">
      {onBack && <BackButton onClick={onBack} />}

      {/* Hero */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[hsl(var(--accent)/0.15)] mb-3">
          <Shield className="h-8 w-8 text-[hsl(var(--accent))]" />
        </div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
          {isPremium ? 'You\'re Ad-Free!' : 'Go Ad-Free'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isPremium ? 'No more ads, plus your own custom profile photo.' : `One-time $${adFreePrice} • Ad-free forever + custom profile photo`}
        </p>
      </div>

      {/* Purchase card */}
      {!isPremium && (
        <div className="bg-card border-2 border-[hsl(var(--accent))] rounded-2xl p-6 mb-6 text-center" data-testid="premium-purchase-card">
          <p className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'var(--font-heading)' }}>{`$${adFreePrice}`}</p>
          <p className="text-xs text-muted-foreground mt-1">One-time payment • Lifetime ad-free</p>
          <Button
            className="mt-4 w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:opacity-90"
            onClick={handleUpgrade}
            disabled={upgrading}
            data-testid="premium-upgrade-btn"
          >
            <Shield className="h-4 w-4 mr-2" />
            {upgrading ? 'Upgrading...' : 'Remove Ads Forever'}
          </Button>
          <p className="text-[10px] text-muted-foreground mt-2">
            No gameplay advantage. Pay-to-win is not our model.
          </p>
        </div>
      )}

      {/* Restore Purchases — cross-platform (works whether you bought on
          web, iOS, or Android; entitlements are tied to your account). */}
      <div className="text-center mb-6">
        <Button
          variant="ghost"
          className="text-xs text-muted-foreground hover:text-foreground"
          onClick={handleRestore}
          disabled={restoring}
          data-testid="premium-restore-btn"
        >
          <Unlock className="h-3.5 w-3.5 mr-1.5" />
          {restoring ? 'Restoring…' : 'Restore Purchases'}
        </Button>
      </div>

      {/* What you get */}
      <div className="space-y-3 mb-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground px-1">What You Get</h2>
        <div className="flex items-center gap-3 bg-card border border-border/60 rounded-xl px-4 py-3" data-testid="premium-feature-adfree">
          <Shield className="h-5 w-5 shrink-0 text-[hsl(var(--accent))]" />
          <div className="flex-1">
            <p className="text-sm font-medium">Ad-Free Experience</p>
            <p className="text-xs text-muted-foreground">No popup ads when switching tabs or searching. Clean, uninterrupted gameplay.</p>
          </div>
          {isPremium ? (
            <Badge className="bg-green-500/15 text-green-700 dark:text-green-300 text-[10px]"><Unlock className="h-3 w-3 mr-1" />Unlocked</Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]"><Lock className="h-3 w-3 mr-1" />Premium</Badge>
          )}
        </div>
        <div className="flex items-center gap-3 bg-card border border-border/60 rounded-xl px-4 py-3" data-testid="premium-feature-photo">
          <Camera className="h-5 w-5 shrink-0 text-[hsl(var(--accent))]" />
          <div className="flex-1">
            <p className="text-sm font-medium">Custom Profile Photo</p>
            <p className="text-xs text-muted-foreground">Upload your own photo (auto safety-checked) instead of an avatar.</p>
          </div>
          {isPremium ? (
            <Badge className="bg-green-500/15 text-green-700 dark:text-green-300 text-[10px]"><Unlock className="h-3 w-3 mr-1" />Unlocked</Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]"><Lock className="h-3 w-3 mr-1" />Premium</Badge>
          )}
        </div>
      </div>
      <div className="space-y-3 mb-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground px-1">Always Free for Everyone</h2>
        {[
          { title: 'Full Gameplay', desc: 'Draft, manage teams, compete in leagues' },
          { title: 'Real-time Scoring', desc: 'Live ₣ updates as events happen' },
          { title: 'Custom Avatar Pack', desc: 'Premium cosmetic avatar styles for your profile' },
          { title: 'All Categories', desc: 'CEOs, Actors, Athletes, Artists, and more' },
        ].map((f, i) => (
          <div key={i} className="flex items-center gap-3 bg-card border border-border/60 rounded-xl px-4 py-3">
            <Check className="h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
            <div className="flex-1">
              <p className="text-sm font-medium">{f.title}</p>
              <p className="text-xs text-muted-foreground">{f.desc}</p>
            </div>
            <Badge className="bg-green-500/15 text-green-700 dark:text-green-300 text-[10px]">Free</Badge>
          </div>
        ))}
      </div>

      {/* Future packages teaser */}
      <div className="space-y-3" data-testid="premium-future-packages">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground px-1 flex items-center gap-1">
          <Sparkles className="h-3.5 w-3.5" /> Coming Soon — Special Category Packs
        </h2>
        <p className="text-xs text-muted-foreground px-1">
          Optional themed people packs you can purchase for custom leagues. Independent of the ad-free upgrade.
        </p>
        {[
          { key: 'race_drivers',       name: 'Race Car Drivers Pack',     desc: 'Verstappen, Hamilton, Norris, Leclerc, Alonso and more elite open-wheel racers' },
          { key: 'comedians',          name: 'Comedians Pack',            desc: 'Stand-ups, late-night hosts, sketch troupes — laughs scored daily' },
          { key: 'scientists',         name: 'Scientists Pack',           desc: 'Nobel laureates, researchers, and breakthrough lab leaders' },
          { key: 'authors',            name: 'Authors Pack',              desc: 'Bestselling novelists, essayists, poets, and journalists' },
          { key: 'reality_tv',         name: 'Reality Stars Pack',        desc: 'Breakout personalities from competition and lifestyle shows — drama scored daily' },
          { key: 'wrestlers_fighters', name: 'Wrestlers & Fighters Pack', desc: 'Pro wrestling icons and MMA champions — Cena, Reigns, McGregor, Adesanya and more' },
        ].map((pkg) => {
          const owned = ownedPackages.includes(pkg.key);
          return (
            <div key={pkg.key} className="flex items-center gap-3 bg-card border border-border/40 rounded-xl px-4 py-3 opacity-70" data-testid={`premium-pack-${pkg.key}`}>
              <Package className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm font-medium">{pkg.name}</p>
                <p className="text-xs text-muted-foreground">{pkg.desc}</p>
              </div>
              <Badge variant="secondary" className="text-[10px]">
                {owned ? 'Owned' : 'Soon'}
              </Badge>
            </div>
          );
        })}
      </div>
    </div>
  );
}
